import { Subject, firstValueFrom } from 'rxjs';
import { validate, ProviderRpcError, ProviderRpcErrorCode } from '@web3-onboard/common';
import { addMessages, init as init$2, getLocaleFromNavigator, _, locale } from 'svelte-i18n';
import merge from 'lodash.merge';
import Joi from 'joi';
import bowser from 'bowser';
import { ethers } from 'ethers';

var time = {
	minutes: "min",
	seconds: "sec"
};
var header = "Transaction Preview";
var timeSection = {
	action: "Simulated",
	past: "ago"
};
var minimized = {
	show: "Show details"
};
var maximized = {
	balanceChangeHeading: "Simulated balance changes for",
	tokenColumnHeader: "Token",
	balanceColumnHeader: "Balance",
	gasUsed: "Gas Used",
	hide: "Hide details",
	cancel: "Cancel",
	confirm: "Confirm"
};
var en = {
	time: time,
	header: header,
	timeSection: timeSection,
	minimized: minimized,
	maximized: maximized
};

function initialize(options) {
    if (options) {
        const { en: customizedEn } = options;
        const merged = merge(en, customizedEn || {});
        addMessages('en', merged);
        const customLocales = Object.keys(options).filter(key => key !== 'en');
        // Sync register all customLocales
        customLocales.forEach(locale => {
            const dictionary = options[locale];
            dictionary && addMessages(locale, dictionary);
        });
    }
    else {
        addMessages('en', en);
    }
    init$2({
        fallbackLocale: 'en',
        initialLocale: getLocaleFromNavigator()
    });
}

const initOptions = Joi.object({
    apiKey: Joi.string().required(),
    sdk: Joi.object().required(),
    containerElement: Joi.string().required(),
    requireTransactionApproval: Joi.boolean(),
    i18n: Joi.object().unknown()
});
const validateTPInit = (request) => validate(initOptions, request);
const transactionPreviewOptions = Joi.object({
    requireTransactionApproval: Joi.boolean(),
    i18n: Joi.object().unknown()
});
const validateTPOptions = (request) => validate(transactionPreviewOptions, request);

/**
 * Takes in TransactionRequest and converts all Hex values to numbers
 * @param transaction
 * @returns a transaction where all Hex properties are now numbers
 */
const hexFieldsToNumber = (transaction) => Object.keys(transaction).reduce((transaction, txnProperty) => ({
    ...transaction,
    ...(typeof transaction[txnProperty] ===
        'string' &&
        transaction[txnProperty].includes('0x') &&
        txnProperty !== 'to' &&
        txnProperty !== 'input' &&
        txnProperty !== 'data' &&
        txnProperty !== 'from'
        ? {
            [txnProperty]: parseInt(transaction[txnProperty], 16)
        }
        : {})
}), transaction);
function getDevice() {
    if (typeof window !== 'undefined') {
        const parsed = bowser.getParser(window.navigator.userAgent);
        const os = parsed.getOS();
        const browser = parsed.getBrowser();
        const { type } = parsed.getPlatform();
        return {
            type: type,
            os: os,
            browser: browser
        };
    }
    else {
        return {
            type: null,
            os: null,
            browser: null
        };
    }
}

const simulateTransactions = async (options, transactions) => {
    const { sdk } = options;
    const cleanedTransactions = transactions.map(transaction => {
        const convertedTransaction = hexFieldsToNumber(transaction);
        const cleanedTrans = {
            from: convertedTransaction.from,
            to: convertedTransaction.to,
            gas: convertedTransaction.gas || convertedTransaction.gasLimit || 0,
            gasPrice: convertedTransaction.gasPrice,
            maxFeePerGas: convertedTransaction.maxFeePerGas,
            maxPriorityFeePerGas: convertedTransaction.maxPriorityFeePerGas,
            input: convertedTransaction.input ||
                transaction.input ||
                transaction.data ||
                '0x',
            value: convertedTransaction.value
        };
        return cleanedTrans;
    });
    return sdk.multiSim(cleanedTransactions);
};

function noop() { }
const identity = x => x;
function run(fn) {
    return fn();
}
function blank_object() {
    return Object.create(null);
}
function run_all(fns) {
    fns.forEach(run);
}
function is_function(thing) {
    return typeof thing === 'function';
}
function safe_not_equal(a, b) {
    return a != a ? b == b : a !== b || ((a && typeof a === 'object') || typeof a === 'function');
}
function is_empty(obj) {
    return Object.keys(obj).length === 0;
}
function subscribe(store, ...callbacks) {
    if (store == null) {
        return noop;
    }
    const unsub = store.subscribe(...callbacks);
    return unsub.unsubscribe ? () => unsub.unsubscribe() : unsub;
}
function component_subscribe(component, store, callback) {
    component.$$.on_destroy.push(subscribe(store, callback));
}
function null_to_empty(value) {
    return value == null ? '' : value;
}

const is_client = typeof window !== 'undefined';
let now = is_client
    ? () => window.performance.now()
    : () => Date.now();
let raf = is_client ? cb => requestAnimationFrame(cb) : noop;

const tasks = new Set();
function run_tasks(now) {
    tasks.forEach(task => {
        if (!task.c(now)) {
            tasks.delete(task);
            task.f();
        }
    });
    if (tasks.size !== 0)
        raf(run_tasks);
}
/**
 * Creates a new task that runs on each raf frame
 * until it returns a falsy value or is aborted
 */
function loop(callback) {
    let task;
    if (tasks.size === 0)
        raf(run_tasks);
    return {
        promise: new Promise(fulfill => {
            tasks.add(task = { c: callback, f: fulfill });
        }),
        abort() {
            tasks.delete(task);
        }
    };
}
function append(target, node) {
    target.appendChild(node);
}
function append_styles(target, style_sheet_id, styles) {
    const append_styles_to = get_root_for_style(target);
    if (!append_styles_to.getElementById(style_sheet_id)) {
        const style = element('style');
        style.id = style_sheet_id;
        style.textContent = styles;
        append_stylesheet(append_styles_to, style);
    }
}
function get_root_for_style(node) {
    if (!node)
        return document;
    const root = node.getRootNode ? node.getRootNode() : node.ownerDocument;
    if (root && root.host) {
        return root;
    }
    return node.ownerDocument;
}
function append_empty_stylesheet(node) {
    const style_element = element('style');
    append_stylesheet(get_root_for_style(node), style_element);
    return style_element.sheet;
}
function append_stylesheet(node, style) {
    append(node.head || node, style);
    return style.sheet;
}
function insert(target, node, anchor) {
    target.insertBefore(node, anchor || null);
}
function detach(node) {
    if (node.parentNode) {
        node.parentNode.removeChild(node);
    }
}
function destroy_each(iterations, detaching) {
    for (let i = 0; i < iterations.length; i += 1) {
        if (iterations[i])
            iterations[i].d(detaching);
    }
}
function element(name) {
    return document.createElement(name);
}
function text(data) {
    return document.createTextNode(data);
}
function space() {
    return text(' ');
}
function empty() {
    return text('');
}
function listen(node, event, handler, options) {
    node.addEventListener(event, handler, options);
    return () => node.removeEventListener(event, handler, options);
}
function stop_propagation(fn) {
    return function (event) {
        event.stopPropagation();
        // @ts-ignore
        return fn.call(this, event);
    };
}
function attr(node, attribute, value) {
    if (value == null)
        node.removeAttribute(attribute);
    else if (node.getAttribute(attribute) !== value)
        node.setAttribute(attribute, value);
}
function children(element) {
    return Array.from(element.childNodes);
}
function set_data(text, data) {
    data = '' + data;
    if (text.data === data)
        return;
    text.data = data;
}
function custom_event(type, detail, { bubbles = false, cancelable = false } = {}) {
    const e = document.createEvent('CustomEvent');
    e.initCustomEvent(type, bubbles, cancelable, detail);
    return e;
}

// we need to store the information for multiple documents because a Svelte application could also contain iframes
// https://github.com/sveltejs/svelte/issues/3624
const managed_styles = new Map();
let active = 0;
// https://github.com/darkskyapp/string-hash/blob/master/index.js
function hash(str) {
    let hash = 5381;
    let i = str.length;
    while (i--)
        hash = ((hash << 5) - hash) ^ str.charCodeAt(i);
    return hash >>> 0;
}
function create_style_information(doc, node) {
    const info = { stylesheet: append_empty_stylesheet(node), rules: {} };
    managed_styles.set(doc, info);
    return info;
}
function create_rule(node, a, b, duration, delay, ease, fn, uid = 0) {
    const step = 16.666 / duration;
    let keyframes = '{\n';
    for (let p = 0; p <= 1; p += step) {
        const t = a + (b - a) * ease(p);
        keyframes += p * 100 + `%{${fn(t, 1 - t)}}\n`;
    }
    const rule = keyframes + `100% {${fn(b, 1 - b)}}\n}`;
    const name = `__svelte_${hash(rule)}_${uid}`;
    const doc = get_root_for_style(node);
    const { stylesheet, rules } = managed_styles.get(doc) || create_style_information(doc, node);
    if (!rules[name]) {
        rules[name] = true;
        stylesheet.insertRule(`@keyframes ${name} ${rule}`, stylesheet.cssRules.length);
    }
    const animation = node.style.animation || '';
    node.style.animation = `${animation ? `${animation}, ` : ''}${name} ${duration}ms linear ${delay}ms 1 both`;
    active += 1;
    return name;
}
function delete_rule(node, name) {
    const previous = (node.style.animation || '').split(', ');
    const next = previous.filter(name
        ? anim => anim.indexOf(name) < 0 // remove specific animation
        : anim => anim.indexOf('__svelte') === -1 // remove all Svelte animations
    );
    const deleted = previous.length - next.length;
    if (deleted) {
        node.style.animation = next.join(', ');
        active -= deleted;
        if (!active)
            clear_rules();
    }
}
function clear_rules() {
    raf(() => {
        if (active)
            return;
        managed_styles.forEach(info => {
            const { ownerNode } = info.stylesheet;
            // there is no ownerNode if it runs on jsdom.
            if (ownerNode)
                detach(ownerNode);
        });
        managed_styles.clear();
    });
}

let current_component;
function set_current_component(component) {
    current_component = component;
}
function get_current_component() {
    if (!current_component)
        throw new Error('Function called outside component initialization');
    return current_component;
}
/**
 * Schedules a callback to run immediately before the component is unmounted.
 *
 * Out of `onMount`, `beforeUpdate`, `afterUpdate` and `onDestroy`, this is the
 * only one that runs inside a server-side component.
 *
 * https://svelte.dev/docs#run-time-svelte-ondestroy
 */
function onDestroy(fn) {
    get_current_component().$$.on_destroy.push(fn);
}

const dirty_components = [];
const binding_callbacks = [];
let render_callbacks = [];
const flush_callbacks = [];
const resolved_promise = /* @__PURE__ */ Promise.resolve();
let update_scheduled = false;
function schedule_update() {
    if (!update_scheduled) {
        update_scheduled = true;
        resolved_promise.then(flush);
    }
}
function add_render_callback(fn) {
    render_callbacks.push(fn);
}
// flush() calls callbacks in this order:
// 1. All beforeUpdate callbacks, in order: parents before children
// 2. All bind:this callbacks, in reverse order: children before parents.
// 3. All afterUpdate callbacks, in order: parents before children. EXCEPT
//    for afterUpdates called during the initial onMount, which are called in
//    reverse order: children before parents.
// Since callbacks might update component values, which could trigger another
// call to flush(), the following steps guard against this:
// 1. During beforeUpdate, any updated components will be added to the
//    dirty_components array and will cause a reentrant call to flush(). Because
//    the flush index is kept outside the function, the reentrant call will pick
//    up where the earlier call left off and go through all dirty components. The
//    current_component value is saved and restored so that the reentrant call will
//    not interfere with the "parent" flush() call.
// 2. bind:this callbacks cannot trigger new flush() calls.
// 3. During afterUpdate, any updated components will NOT have their afterUpdate
//    callback called a second time; the seen_callbacks set, outside the flush()
//    function, guarantees this behavior.
const seen_callbacks = new Set();
let flushidx = 0; // Do *not* move this inside the flush() function
function flush() {
    // Do not reenter flush while dirty components are updated, as this can
    // result in an infinite loop. Instead, let the inner flush handle it.
    // Reentrancy is ok afterwards for bindings etc.
    if (flushidx !== 0) {
        return;
    }
    const saved_component = current_component;
    do {
        // first, call beforeUpdate functions
        // and update components
        try {
            while (flushidx < dirty_components.length) {
                const component = dirty_components[flushidx];
                flushidx++;
                set_current_component(component);
                update(component.$$);
            }
        }
        catch (e) {
            // reset dirty state to not end up in a deadlocked state and then rethrow
            dirty_components.length = 0;
            flushidx = 0;
            throw e;
        }
        set_current_component(null);
        dirty_components.length = 0;
        flushidx = 0;
        while (binding_callbacks.length)
            binding_callbacks.pop()();
        // then, once components are updated, call
        // afterUpdate functions. This may cause
        // subsequent updates...
        for (let i = 0; i < render_callbacks.length; i += 1) {
            const callback = render_callbacks[i];
            if (!seen_callbacks.has(callback)) {
                // ...so guard against infinite loops
                seen_callbacks.add(callback);
                callback();
            }
        }
        render_callbacks.length = 0;
    } while (dirty_components.length);
    while (flush_callbacks.length) {
        flush_callbacks.pop()();
    }
    update_scheduled = false;
    seen_callbacks.clear();
    set_current_component(saved_component);
}
function update($$) {
    if ($$.fragment !== null) {
        $$.update();
        run_all($$.before_update);
        const dirty = $$.dirty;
        $$.dirty = [-1];
        $$.fragment && $$.fragment.p($$.ctx, dirty);
        $$.after_update.forEach(add_render_callback);
    }
}
/**
 * Useful for example to execute remaining `afterUpdate` callbacks before executing `destroy`.
 */
function flush_render_callbacks(fns) {
    const filtered = [];
    const targets = [];
    render_callbacks.forEach((c) => fns.indexOf(c) === -1 ? filtered.push(c) : targets.push(c));
    targets.forEach((c) => c());
    render_callbacks = filtered;
}

let promise;
function wait() {
    if (!promise) {
        promise = Promise.resolve();
        promise.then(() => {
            promise = null;
        });
    }
    return promise;
}
function dispatch(node, direction, kind) {
    node.dispatchEvent(custom_event(`${direction ? 'intro' : 'outro'}${kind}`));
}
const outroing = new Set();
let outros;
function group_outros() {
    outros = {
        r: 0,
        c: [],
        p: outros // parent group
    };
}
function check_outros() {
    if (!outros.r) {
        run_all(outros.c);
    }
    outros = outros.p;
}
function transition_in(block, local) {
    if (block && block.i) {
        outroing.delete(block);
        block.i(local);
    }
}
function transition_out(block, local, detach, callback) {
    if (block && block.o) {
        if (outroing.has(block))
            return;
        outroing.add(block);
        outros.c.push(() => {
            outroing.delete(block);
            if (callback) {
                if (detach)
                    block.d(1);
                callback();
            }
        });
        block.o(local);
    }
    else if (callback) {
        callback();
    }
}
const null_transition = { duration: 0 };
function create_in_transition(node, fn, params) {
    const options = { direction: 'in' };
    let config = fn(node, params, options);
    let running = false;
    let animation_name;
    let task;
    let uid = 0;
    function cleanup() {
        if (animation_name)
            delete_rule(node, animation_name);
    }
    function go() {
        const { delay = 0, duration = 300, easing = identity, tick = noop, css } = config || null_transition;
        if (css)
            animation_name = create_rule(node, 0, 1, duration, delay, easing, css, uid++);
        tick(0, 1);
        const start_time = now() + delay;
        const end_time = start_time + duration;
        if (task)
            task.abort();
        running = true;
        add_render_callback(() => dispatch(node, true, 'start'));
        task = loop(now => {
            if (running) {
                if (now >= end_time) {
                    tick(1, 0);
                    dispatch(node, true, 'end');
                    cleanup();
                    return running = false;
                }
                if (now >= start_time) {
                    const t = easing((now - start_time) / duration);
                    tick(t, 1 - t);
                }
            }
            return running;
        });
    }
    let started = false;
    return {
        start() {
            if (started)
                return;
            started = true;
            delete_rule(node);
            if (is_function(config)) {
                config = config(options);
                wait().then(go);
            }
            else {
                go();
            }
        },
        invalidate() {
            started = false;
        },
        end() {
            if (running) {
                cleanup();
                running = false;
            }
        }
    };
}
function create_component(block) {
    block && block.c();
}
function mount_component(component, target, anchor, customElement) {
    const { fragment, after_update } = component.$$;
    fragment && fragment.m(target, anchor);
    if (!customElement) {
        // onMount happens before the initial afterUpdate
        add_render_callback(() => {
            const new_on_destroy = component.$$.on_mount.map(run).filter(is_function);
            // if the component was destroyed immediately
            // it will update the `$$.on_destroy` reference to `null`.
            // the destructured on_destroy may still reference to the old array
            if (component.$$.on_destroy) {
                component.$$.on_destroy.push(...new_on_destroy);
            }
            else {
                // Edge case - component was destroyed immediately,
                // most likely as a result of a binding initialising
                run_all(new_on_destroy);
            }
            component.$$.on_mount = [];
        });
    }
    after_update.forEach(add_render_callback);
}
function destroy_component(component, detaching) {
    const $$ = component.$$;
    if ($$.fragment !== null) {
        flush_render_callbacks($$.after_update);
        run_all($$.on_destroy);
        $$.fragment && $$.fragment.d(detaching);
        // TODO null out other refs, including component.$$ (but need to
        // preserve final state?)
        $$.on_destroy = $$.fragment = null;
        $$.ctx = [];
    }
}
function make_dirty(component, i) {
    if (component.$$.dirty[0] === -1) {
        dirty_components.push(component);
        schedule_update();
        component.$$.dirty.fill(0);
    }
    component.$$.dirty[(i / 31) | 0] |= (1 << (i % 31));
}
function init$1(component, options, instance, create_fragment, not_equal, props, append_styles, dirty = [-1]) {
    const parent_component = current_component;
    set_current_component(component);
    const $$ = component.$$ = {
        fragment: null,
        ctx: [],
        // state
        props,
        update: noop,
        not_equal,
        bound: blank_object(),
        // lifecycle
        on_mount: [],
        on_destroy: [],
        on_disconnect: [],
        before_update: [],
        after_update: [],
        context: new Map(options.context || (parent_component ? parent_component.$$.context : [])),
        // everything else
        callbacks: blank_object(),
        dirty,
        skip_bound: false,
        root: options.target || parent_component.$$.root
    };
    append_styles && append_styles($$.root);
    let ready = false;
    $$.ctx = instance
        ? instance(component, options.props || {}, (i, ret, ...rest) => {
            const value = rest.length ? rest[0] : ret;
            if ($$.ctx && not_equal($$.ctx[i], $$.ctx[i] = value)) {
                if (!$$.skip_bound && $$.bound[i])
                    $$.bound[i](value);
                if (ready)
                    make_dirty(component, i);
            }
            return ret;
        })
        : [];
    $$.update();
    ready = true;
    run_all($$.before_update);
    // `false` as a special case of no DOM component
    $$.fragment = create_fragment ? create_fragment($$.ctx) : false;
    if (options.target) {
        if (options.hydrate) {
            const nodes = children(options.target);
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            $$.fragment && $$.fragment.l(nodes);
            nodes.forEach(detach);
        }
        else {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            $$.fragment && $$.fragment.c();
        }
        if (options.intro)
            transition_in(component.$$.fragment);
        mount_component(component, options.target, options.anchor, options.customElement);
        flush();
    }
    set_current_component(parent_component);
}
/**
 * Base class for Svelte components. Used when dev=false.
 */
class SvelteComponent {
    $destroy() {
        destroy_component(this, 1);
        this.$destroy = noop;
    }
    $on(type, callback) {
        if (!is_function(callback)) {
            return noop;
        }
        const callbacks = (this.$$.callbacks[type] || (this.$$.callbacks[type] = []));
        callbacks.push(callback);
        return () => {
            const index = callbacks.indexOf(callback);
            if (index !== -1)
                callbacks.splice(index, 1);
        };
    }
    $set($$props) {
        if (this.$$set && !is_empty($$props)) {
            this.$$.skip_bound = true;
            this.$$set($$props);
            this.$$.skip_bound = false;
        }
    }
}

function cubicOut(t) {
    const f = t - 1.0;
    return f * f * f + 1.0;
}

function fade(node, { delay = 0, duration = 400, easing = identity } = {}) {
    const o = +getComputedStyle(node).opacity;
    return {
        delay,
        duration,
        easing,
        css: t => `opacity: ${t * o}`
    };
}

/* src/views/components/IconBadge.svelte generated by Svelte v3.59.1 */

function add_css$6(target) {
	append_styles(target, "svelte-pgfnfr", "div.notification-icons-wrapper.svelte-pgfnfr{height:2rem;width:2rem;background:var(--border-color);border:2px solid var(--border-color);font-size:1rem}.border.svelte-pgfnfr{border-radius:8px}");
}

function create_fragment$6(ctx) {
	let div1;

	return {
		c() {
			div1 = element("div");
			div1.innerHTML = `<div class="flex items-center justify-center border relative notification-icons-wrapper svelte-pgfnfr">🔮</div>`;
			attr(div1, "class", "relative");
		},
		m(target, anchor) {
			insert(target, div1, anchor);
		},
		p: noop,
		i: noop,
		o: noop,
		d(detaching) {
			if (detaching) detach(div1);
		}
	};
}

function instance$6($$self) {
	return [];
}

class IconBadge extends SvelteComponent {
	constructor(options) {
		super();
		init$1(this, options, instance$6, create_fragment$6, safe_not_equal, {}, add_css$6);
	}
}

/* src/views/components/Button.svelte generated by Svelte v3.59.1 */

function add_css$5(target) {
	append_styles(target, "svelte-1aecns7", "button.svelte-1aecns7{display:flex;flex-direction:row;align-items:center;padding:0.25rem 0.75rem;gap:0.25rem;border-radius:var(--onboard-border-radius-5, var(--border-radius-5));background:transparent;color:var(--action-color)}button.svelte-1aecns7:hover{background:var(--border-color, rgba(146, 155, 237, 0.2))}");
}

function create_fragment$5(ctx) {
	let button;
	let t;
	let mounted;
	let dispose;

	return {
		c() {
			button = element("button");
			t = text(/*btnText*/ ctx[1]);
			attr(button, "class", "svelte-1aecns7");
		},
		m(target, anchor) {
			insert(target, button, anchor);
			append(button, t);

			if (!mounted) {
				dispose = listen(button, "click", stop_propagation(/*click_handler*/ ctx[2]));
				mounted = true;
			}
		},
		p(ctx, [dirty]) {
			if (dirty & /*btnText*/ 2) set_data(t, /*btnText*/ ctx[1]);
		},
		i: noop,
		o: noop,
		d(detaching) {
			if (detaching) detach(button);
			mounted = false;
			dispose();
		}
	};
}

function instance$5($$self, $$props, $$invalidate) {
	let { btnFunction } = $$props;
	let { btnText } = $$props;
	const click_handler = () => btnFunction(true);

	$$self.$$set = $$props => {
		if ('btnFunction' in $$props) $$invalidate(0, btnFunction = $$props.btnFunction);
		if ('btnText' in $$props) $$invalidate(1, btnText = $$props.btnText);
	};

	return [btnFunction, btnText, click_handler];
}

class Button extends SvelteComponent {
	constructor(options) {
		super();
		init$1(this, options, instance$5, create_fragment$5, safe_not_equal, { btnFunction: 0, btnText: 1 }, add_css$5);
	}
}

var closeIcon = `
<svg width="100%" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M10 0C4.47 0 0 4.47 0 10C0 15.53 4.47 20 10 20C15.53 20 20 15.53 20 10C20 4.47 15.53 0 10 0ZM15 13.59L13.59 15L10 11.41L6.41 15L5 13.59L8.59 10L5 6.41L6.41 5L10 8.59L13.59 5L15 6.41L11.41 10L15 13.59Z" fill="currentColor"/>
</svg>
`;

/* src/views/components/Timer.svelte generated by Svelte v3.59.1 */

function add_css$4(target) {
	append_styles(target, "svelte-biovyh", "div.svelte-biovyh{display:flex;justify-content:center;font-size:inherit;font-family:inherit}span.svelte-biovyh{font-family:inherit;display:flex;align-items:center;margin:0 2px}.time.svelte-biovyh{color:inherit}.stale.svelte-biovyh{color:var(--onboard-warning-300, var(--warning-300))}");
}

// (52:2) {#if startTime}
function create_if_block$2(ctx) {
	let t0;
	let span;
	let t1_value = /*timeString*/ ctx[4](/*currentTime*/ ctx[1] - /*startTime*/ ctx[0]) + "";
	let t1;
	let t2;
	let t3_value = /*$formatter*/ ctx[2]('timeSection.past', { default: en.timeSection.past }) + "";
	let t3;

	return {
		c() {
			t0 = text("-\n    ");
			span = element("span");
			t1 = text(t1_value);
			t2 = space();
			t3 = text(t3_value);
			attr(span, "class", "svelte-biovyh");
		},
		m(target, anchor) {
			insert(target, t0, anchor);
			insert(target, span, anchor);
			append(span, t1);
			insert(target, t2, anchor);
			insert(target, t3, anchor);
		},
		p(ctx, dirty) {
			if (dirty & /*currentTime, startTime*/ 3 && t1_value !== (t1_value = /*timeString*/ ctx[4](/*currentTime*/ ctx[1] - /*startTime*/ ctx[0]) + "")) set_data(t1, t1_value);
			if (dirty & /*$formatter*/ 4 && t3_value !== (t3_value = /*$formatter*/ ctx[2]('timeSection.past', { default: en.timeSection.past }) + "")) set_data(t3, t3_value);
		},
		d(detaching) {
			if (detaching) detach(t0);
			if (detaching) detach(span);
			if (detaching) detach(t2);
			if (detaching) detach(t3);
		}
	};
}

function create_fragment$4(ctx) {
	let div;
	let t0_value = /*$formatter*/ ctx[2]('timeSection.action', { default: en.timeSection.action }) + "";
	let t0;
	let t1;
	let div_class_value;
	let if_block = /*startTime*/ ctx[0] && create_if_block$2(ctx);

	return {
		c() {
			div = element("div");
			t0 = text(t0_value);
			t1 = space();
			if (if_block) if_block.c();
			attr(div, "class", div_class_value = "" + (null_to_empty(`time ${/*formatSeconds*/ ctx[3](/*currentTime*/ ctx[1] - /*startTime*/ ctx[0]) >= 60 && 'stale'}`) + " svelte-biovyh"));
		},
		m(target, anchor) {
			insert(target, div, anchor);
			append(div, t0);
			append(div, t1);
			if (if_block) if_block.m(div, null);
		},
		p(ctx, [dirty]) {
			if (dirty & /*$formatter*/ 4 && t0_value !== (t0_value = /*$formatter*/ ctx[2]('timeSection.action', { default: en.timeSection.action }) + "")) set_data(t0, t0_value);

			if (/*startTime*/ ctx[0]) {
				if (if_block) {
					if_block.p(ctx, dirty);
				} else {
					if_block = create_if_block$2(ctx);
					if_block.c();
					if_block.m(div, null);
				}
			} else if (if_block) {
				if_block.d(1);
				if_block = null;
			}

			if (dirty & /*currentTime, startTime*/ 3 && div_class_value !== (div_class_value = "" + (null_to_empty(`time ${/*formatSeconds*/ ctx[3](/*currentTime*/ ctx[1] - /*startTime*/ ctx[0]) >= 60 && 'stale'}`) + " svelte-biovyh"))) {
				attr(div, "class", div_class_value);
			}
		},
		i: noop,
		o: noop,
		d(detaching) {
			if (detaching) detach(div);
			if (if_block) if_block.d();
		}
	};
}

function instance$4($$self, $$props, $$invalidate) {
	let $formatter;
	let $locale;
	component_subscribe($$self, _, $$value => $$invalidate(2, $formatter = $$value));
	component_subscribe($$self, locale, $$value => $$invalidate(5, $locale = $$value));
	let { startTime } = $$props;

	const formatSeconds = time => {
		const seconds = Math.floor(time / 1000);
		return seconds < 0 ? 0 : seconds;
	};

	const timeString = time => {
		const formattedSeconds = formatSeconds(time);

		return formattedSeconds >= 60
		? `${Math.floor(formattedSeconds / 60).toLocaleString($locale)} ${$formatter('time.minutes')}`
		: `${formattedSeconds.toLocaleString($locale)} ${$formatter('time.seconds')}`;
	};

	let currentTime = Date.now();

	const intervalId = setInterval(
		() => {
			$$invalidate(1, currentTime = Date.now());
		},
		1000
	);

	onDestroy(() => {
		clearInterval(intervalId);
	});

	$$self.$$set = $$props => {
		if ('startTime' in $$props) $$invalidate(0, startTime = $$props.startTime);
	};

	return [startTime, currentTime, $formatter, formatSeconds, timeString];
}

class Timer extends SvelteComponent {
	constructor(options) {
		super();
		init$1(this, options, instance$4, create_fragment$4, safe_not_equal, { startTime: 0 }, add_css$4);
	}
}

/* src/views/components/SimulationHeader.svelte generated by Svelte v3.59.1 */

function add_css$3(target) {
	append_styles(target, "svelte-1hugz8g", "div.notify-transaction-data.svelte-1hugz8g{font-size:var(--onboard-font-size-6, var(--font-size-6));font-family:inherit;margin:0px 20px 0px 8px;justify-content:center;gap:4px}.timer.svelte-1hugz8g{display:inline-flex;font-size:var(--onboard-font-size-7, var(--font-size-7));line-height:var(--onboard-font-line-height-4, var(--font-line-height-4))}.transaction-status.svelte-1hugz8g{line-height:var(--onboard-font-size-5, var(--font-size-5));font-weight:400;overflow:hidden;text-overflow:ellipsis;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical}");
}

function create_fragment$3(ctx) {
	let div;
	let span0;
	let t0_value = /*$_*/ ctx[1]('header', { default: en.header }) + "";
	let t0;
	let t1;
	let span1;
	let timer;
	let current;

	timer = new Timer({
			props: { startTime: /*startTime*/ ctx[0] }
		});

	return {
		c() {
			div = element("div");
			span0 = element("span");
			t0 = text(t0_value);
			t1 = space();
			span1 = element("span");
			create_component(timer.$$.fragment);
			attr(span0, "class", "transaction-status svelte-1hugz8g");
			attr(span1, "class", "timer svelte-1hugz8g");
			attr(div, "class", "flex flex-column notify-transaction-data svelte-1hugz8g");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			append(div, span0);
			append(span0, t0);
			append(div, t1);
			append(div, span1);
			mount_component(timer, span1, null);
			current = true;
		},
		p(ctx, [dirty]) {
			if ((!current || dirty & /*$_*/ 2) && t0_value !== (t0_value = /*$_*/ ctx[1]('header', { default: en.header }) + "")) set_data(t0, t0_value);
			const timer_changes = {};
			if (dirty & /*startTime*/ 1) timer_changes.startTime = /*startTime*/ ctx[0];
			timer.$set(timer_changes);
		},
		i(local) {
			if (current) return;
			transition_in(timer.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(timer.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			destroy_component(timer);
		}
	};
}

function instance$3($$self, $$props, $$invalidate) {
	let $_;
	component_subscribe($$self, _, $$value => $$invalidate(1, $_ = $$value));
	let { startTime } = $$props;

	$$self.$$set = $$props => {
		if ('startTime' in $$props) $$invalidate(0, startTime = $$props.startTime);
	};

	return [startTime, $_];
}

class SimulationHeader extends SvelteComponent {
	constructor(options) {
		super();
		init$1(this, options, instance$3, create_fragment$3, safe_not_equal, { startTime: 0 }, add_css$3);
	}
}

/* src/views/Maximized.svelte generated by Svelte v3.59.1 */

function add_css$2(target) {
	append_styles(target, "svelte-15gl6rk", ".maximized.svelte-15gl6rk.svelte-15gl6rk{pointer-events:all;width:100%;min-height:3.5rem;display:flex;flex-direction:column;position:relative;overflow:hidden}.radius.svelte-15gl6rk.svelte-15gl6rk{border-radius:inherit}div.tp-close-btn.svelte-15gl6rk.svelte-15gl6rk{visibility:visible;opacity:1}div.tp-close-btn.svelte-15gl6rk.svelte-15gl6rk{margin-left:auto;margin-bottom:auto;height:1.5rem;width:1.5rem;position:absolute;top:0.5rem;right:0.5rem;justify-content:center;align-items:center}div.maximized.svelte-15gl6rk:hover>div.tp-close-btn-desktop.svelte-15gl6rk{visibility:visible;opacity:1}div.tp-close-btn-desktop.svelte-15gl6rk.svelte-15gl6rk{visibility:hidden;transition:visibility 0.15s linear, opacity 0.15s linear;opacity:0}.tp-close-btn.svelte-15gl6rk .close-icon.svelte-15gl6rk{width:1.25rem;margin:auto}.tp-close-btn.svelte-15gl6rk .close-icon.svelte-15gl6rk{color:currentColor}.tp-close-btn.svelte-15gl6rk:hover .close-icon.svelte-15gl6rk{color:currentColor}.table-radius.svelte-15gl6rk.svelte-15gl6rk{border-radius:var(--border-radius-5)}.bn-notify-notification-inner.svelte-15gl6rk.svelte-15gl6rk{padding:0.75rem;background:var(--foreground-color)}.details.svelte-15gl6rk.svelte-15gl6rk{display:flex;flex-direction:column;padding:0.75rem;gap:0.5rem;border-top:1px solid var(--border-color)}.address-info.svelte-15gl6rk.svelte-15gl6rk{font-size:0.75rem;font-weight:400;line-height:1rem;display:inline-flex}.details-cta.svelte-15gl6rk.svelte-15gl6rk{color:inherit;font-weight:600;font-size:0.875rem;display:flex;justify-content:flex-end;flex-direction:row;align-items:center;padding:0.5rem;gap:0.5rem;height:3rem;border-top:1px solid var(--border-color);flex:none;order:2;align-self:stretch;flex-grow:0}table.balance-change-table.svelte-15gl6rk.svelte-15gl6rk{width:100%;overflow:hidden;border-spacing:0;border:1px solid transparent;border-color:var(--border-color);color:var(--text-color)}table.balance-change-table.svelte-15gl6rk td.svelte-15gl6rk,table.balance-change-table.svelte-15gl6rk th.svelte-15gl6rk{padding:0.25rem 1rem;text-align:start;line-height:1rem}table.balance-change-table.svelte-15gl6rk th.svelte-15gl6rk{font-size:0.75rem}table.balance-change-table.svelte-15gl6rk td.svelte-15gl6rk{font-size:0.875rem}table.balance-change-table.svelte-15gl6rk td.token-text.svelte-15gl6rk{font-weight:600}tbody.svelte-15gl6rk>tr.svelte-15gl6rk:not(:first-child){box-shadow:inset 0px 1px 0px var(--border-color)}table.balance-change-table.svelte-15gl6rk thead.svelte-15gl6rk{background:var(--border-color)}.negative.svelte-15gl6rk.svelte-15gl6rk{color:var(--onboard-danger-400, var(--danger-400))}.positive.svelte-15gl6rk.svelte-15gl6rk{color:var(--onboard-success-500, var(--success-500))}.gas-used-value.svelte-15gl6rk.svelte-15gl6rk{color:var(--text-color);display:inline-block;margin:0 4px 0 0}");
}

function get_each_context(ctx, list, i) {
	const child_ctx = ctx.slice();
	child_ctx[24] = list[i];
	return child_ctx;
}

function get_each_context_1(ctx, list, i) {
	const child_ctx = ctx.slice();
	child_ctx[27] = list[i];
	return child_ctx;
}

// (218:2) {#if !requireTransactionApproval}
function create_if_block_3(ctx) {
	let div1;
	let div0;
	let mounted;
	let dispose;

	return {
		c() {
			div1 = element("div");
			div0 = element("div");
			attr(div0, "class", "flex items-center close-icon svelte-15gl6rk");
			attr(div1, "class", "tp-close-btn tp-close-btn-" + /*device*/ ctx[8].type + " pointer flex" + " svelte-15gl6rk");
		},
		m(target, anchor) {
			insert(target, div1, anchor);
			append(div1, div0);
			div0.innerHTML = closeIcon;

			if (!mounted) {
				dispose = listen(div1, "click", stop_propagation(/*click_handler*/ ctx[14]));
				mounted = true;
			}
		},
		p: noop,
		d(detaching) {
			if (detaching) detach(div1);
			mounted = false;
			dispose();
		}
	};
}

// (261:8) {#if balanceChanges.length}
function create_if_block_1(ctx) {
	let t;
	let if_block_anchor;
	let each_value = /*balanceChanges*/ ctx[12];
	let each_blocks = [];

	for (let i = 0; i < each_value.length; i += 1) {
		each_blocks[i] = create_each_block(get_each_context(ctx, each_value, i));
	}

	let if_block = /*totalGasInEth*/ ctx[5] && /*totalGasUsed*/ ctx[6] && create_if_block_2(ctx);

	return {
		c() {
			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].c();
			}

			t = space();
			if (if_block) if_block.c();
			if_block_anchor = empty();
		},
		m(target, anchor) {
			for (let i = 0; i < each_blocks.length; i += 1) {
				if (each_blocks[i]) {
					each_blocks[i].m(target, anchor);
				}
			}

			insert(target, t, anchor);
			if (if_block) if_block.m(target, anchor);
			insert(target, if_block_anchor, anchor);
		},
		p(ctx, dirty) {
			if (dirty & /*balanceChanges, cleanBalance*/ 4608) {
				each_value = /*balanceChanges*/ ctx[12];
				let i;

				for (i = 0; i < each_value.length; i += 1) {
					const child_ctx = get_each_context(ctx, each_value, i);

					if (each_blocks[i]) {
						each_blocks[i].p(child_ctx, dirty);
					} else {
						each_blocks[i] = create_each_block(child_ctx);
						each_blocks[i].c();
						each_blocks[i].m(t.parentNode, t);
					}
				}

				for (; i < each_blocks.length; i += 1) {
					each_blocks[i].d(1);
				}

				each_blocks.length = each_value.length;
			}

			if (/*totalGasInEth*/ ctx[5] && /*totalGasUsed*/ ctx[6]) {
				if (if_block) {
					if_block.p(ctx, dirty);
				} else {
					if_block = create_if_block_2(ctx);
					if_block.c();
					if_block.m(if_block_anchor.parentNode, if_block_anchor);
				}
			} else if (if_block) {
				if_block.d(1);
				if_block = null;
			}
		},
		d(detaching) {
			destroy_each(each_blocks, detaching);
			if (detaching) detach(t);
			if (if_block) if_block.d(detaching);
			if (detaching) detach(if_block_anchor);
		}
	};
}

// (263:12) {#each assetChanges.balanceChanges as asset}
function create_each_block_1(ctx) {
	let tr;
	let td0;
	let t0_value = /*asset*/ ctx[27].asset.symbol + "";
	let t0;
	let t1;
	let td1;
	let t2_value = (!/*asset*/ ctx[27].delta.includes('-') ? '+' : '') + "";
	let t2;
	let t3_value = /*cleanBalance*/ ctx[9](/*asset*/ ctx[27].delta) + "";
	let t3;

	return {
		c() {
			tr = element("tr");
			td0 = element("td");
			t0 = text(t0_value);
			t1 = space();
			td1 = element("td");
			t2 = text(t2_value);
			t3 = text(t3_value);
			attr(td0, "class", "token-text svelte-15gl6rk");

			attr(td1, "class", "" + (null_to_empty(/*asset*/ ctx[27].delta.includes('-')
			? 'negative'
			: 'positive') + " svelte-15gl6rk"));

			attr(tr, "class", "svelte-15gl6rk");
		},
		m(target, anchor) {
			insert(target, tr, anchor);
			append(tr, td0);
			append(td0, t0);
			append(tr, t1);
			append(tr, td1);
			append(td1, t2);
			append(td1, t3);
		},
		p: noop,
		d(detaching) {
			if (detaching) detach(tr);
		}
	};
}

// (262:10) {#each balanceChanges as assetChanges}
function create_each_block(ctx) {
	let each_1_anchor;
	let each_value_1 = /*assetChanges*/ ctx[24].balanceChanges;
	let each_blocks = [];

	for (let i = 0; i < each_value_1.length; i += 1) {
		each_blocks[i] = create_each_block_1(get_each_context_1(ctx, each_value_1, i));
	}

	return {
		c() {
			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].c();
			}

			each_1_anchor = empty();
		},
		m(target, anchor) {
			for (let i = 0; i < each_blocks.length; i += 1) {
				if (each_blocks[i]) {
					each_blocks[i].m(target, anchor);
				}
			}

			insert(target, each_1_anchor, anchor);
		},
		p(ctx, dirty) {
			if (dirty & /*balanceChanges, cleanBalance*/ 4608) {
				each_value_1 = /*assetChanges*/ ctx[24].balanceChanges;
				let i;

				for (i = 0; i < each_value_1.length; i += 1) {
					const child_ctx = get_each_context_1(ctx, each_value_1, i);

					if (each_blocks[i]) {
						each_blocks[i].p(child_ctx, dirty);
					} else {
						each_blocks[i] = create_each_block_1(child_ctx);
						each_blocks[i].c();
						each_blocks[i].m(each_1_anchor.parentNode, each_1_anchor);
					}
				}

				for (; i < each_blocks.length; i += 1) {
					each_blocks[i].d(1);
				}

				each_blocks.length = each_value_1.length;
			}
		},
		d(detaching) {
			destroy_each(each_blocks, detaching);
			if (detaching) detach(each_1_anchor);
		}
	};
}

// (274:10) {#if totalGasInEth && totalGasUsed}
function create_if_block_2(ctx) {
	let tr;
	let td0;
	let t1;
	let td1;
	let t2;
	let t3;
	let t4;
	let div;
	let t5;
	let t6;
	let t7;
	let t8_value = /*$_*/ ctx[7]('maximized.gasUsed', { default: en.maximized.gasUsed }) + "";
	let t8;
	let t9;

	return {
		c() {
			tr = element("tr");
			td0 = element("td");
			td0.textContent = "ETH";
			t1 = space();
			td1 = element("td");
			t2 = text("-");
			t3 = text(/*totalGasInEth*/ ctx[5]);
			t4 = space();
			div = element("div");
			t5 = text("(");
			t6 = text(/*totalGasUsed*/ ctx[6]);
			t7 = space();
			t8 = text(t8_value);
			t9 = text(")");
			attr(td0, "class", "token-text svelte-15gl6rk");
			attr(div, "class", "gas-used-value svelte-15gl6rk");
			attr(td1, "class", "negative svelte-15gl6rk");
			attr(tr, "class", "svelte-15gl6rk");
		},
		m(target, anchor) {
			insert(target, tr, anchor);
			append(tr, td0);
			append(tr, t1);
			append(tr, td1);
			append(td1, t2);
			append(td1, t3);
			append(td1, t4);
			append(td1, div);
			append(div, t5);
			append(div, t6);
			append(div, t7);
			append(div, t8);
			append(div, t9);
		},
		p(ctx, dirty) {
			if (dirty & /*totalGasInEth*/ 32) set_data(t3, /*totalGasInEth*/ ctx[5]);
			if (dirty & /*totalGasUsed*/ 64) set_data(t6, /*totalGasUsed*/ ctx[6]);
			if (dirty & /*$_*/ 128 && t8_value !== (t8_value = /*$_*/ ctx[7]('maximized.gasUsed', { default: en.maximized.gasUsed }) + "")) set_data(t8, t8_value);
		},
		d(detaching) {
			if (detaching) detach(tr);
		}
	};
}

// (306:4) {:else}
function create_else_block$1(ctx) {
	let button;
	let current;

	button = new Button({
			props: {
				btnText: /*$_*/ ctx[7]('maximized.hide', { default: en.maximized.hide }),
				btnFunction: /*func_2*/ ctx[17]
			}
		});

	return {
		c() {
			create_component(button.$$.fragment);
		},
		m(target, anchor) {
			mount_component(button, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const button_changes = {};
			if (dirty & /*$_*/ 128) button_changes.btnText = /*$_*/ ctx[7]('maximized.hide', { default: en.maximized.hide });
			if (dirty & /*toggleExpanded*/ 4) button_changes.btnFunction = /*func_2*/ ctx[17];
			button.$set(button_changes);
		},
		i(local) {
			if (current) return;
			transition_in(button.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(button.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(button, detaching);
		}
	};
}

// (293:4) {#if requireTransactionApproval}
function create_if_block$1(ctx) {
	let button0;
	let t;
	let button1;
	let current;

	button0 = new Button({
			props: {
				btnText: /*$_*/ ctx[7]('maximized.cancel', { default: en.maximized.cancel }),
				btnFunction: /*func*/ ctx[15]
			}
		});

	button1 = new Button({
			props: {
				btnText: /*$_*/ ctx[7]('maximized.confirm', { default: en.maximized.confirm }),
				btnFunction: /*func_1*/ ctx[16]
			}
		});

	return {
		c() {
			create_component(button0.$$.fragment);
			t = space();
			create_component(button1.$$.fragment);
		},
		m(target, anchor) {
			mount_component(button0, target, anchor);
			insert(target, t, anchor);
			mount_component(button1, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const button0_changes = {};
			if (dirty & /*$_*/ 128) button0_changes.btnText = /*$_*/ ctx[7]('maximized.cancel', { default: en.maximized.cancel });
			if (dirty & /*transactionApproved*/ 2) button0_changes.btnFunction = /*func*/ ctx[15];
			button0.$set(button0_changes);
			const button1_changes = {};
			if (dirty & /*$_*/ 128) button1_changes.btnText = /*$_*/ ctx[7]('maximized.confirm', { default: en.maximized.confirm });
			if (dirty & /*transactionApproved*/ 2) button1_changes.btnFunction = /*func_1*/ ctx[16];
			button1.$set(button1_changes);
		},
		i(local) {
			if (current) return;
			transition_in(button0.$$.fragment, local);
			transition_in(button1.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(button0.$$.fragment, local);
			transition_out(button1.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(button0, detaching);
			if (detaching) detach(t);
			destroy_component(button1, detaching);
		}
	};
}

function create_fragment$2(ctx) {
	let div4;
	let t0;
	let div0;
	let iconbadge;
	let t1;
	let simulationheader;
	let t2;
	let div2;
	let div1;

	let t3_value = /*$_*/ ctx[7]('maximized.balanceChangeHeading', {
		default: en.maximized.balanceChangeHeading
	}) + "";

	let t3;
	let t4;
	let t5_value = /*shortenAddress*/ ctx[10](/*transactionOriginator*/ ctx[11]) + "";
	let t5;
	let t6;
	let table;
	let colgroup;
	let t8;
	let thead;
	let tr;
	let th0;
	let t9_value = /*$_*/ ctx[7]('maximized.tokenColumnHeader', { default: en.maximized.tokenColumnHeader }) + "";
	let t9;
	let t10;
	let th1;

	let t11_value = /*$_*/ ctx[7]('maximized.balanceColumnHeader', {
		default: en.maximized.balanceColumnHeader
	}) + "";

	let t11;
	let t12;
	let tbody;
	let t13;
	let div3;
	let current_block_type_index;
	let if_block2;
	let current;
	let if_block0 = !/*requireTransactionApproval*/ ctx[0] && create_if_block_3(ctx);
	iconbadge = new IconBadge({});

	simulationheader = new SimulationHeader({
			props: { startTime: /*startTime*/ ctx[4] }
		});

	let if_block1 = /*balanceChanges*/ ctx[12].length && create_if_block_1(ctx);
	const if_block_creators = [create_if_block$1, create_else_block$1];
	const if_blocks = [];

	function select_block_type(ctx, dirty) {
		if (/*requireTransactionApproval*/ ctx[0]) return 0;
		return 1;
	}

	current_block_type_index = select_block_type(ctx);
	if_block2 = if_blocks[current_block_type_index] = if_block_creators[current_block_type_index](ctx);

	return {
		c() {
			div4 = element("div");
			if (if_block0) if_block0.c();
			t0 = space();
			div0 = element("div");
			create_component(iconbadge.$$.fragment);
			t1 = space();
			create_component(simulationheader.$$.fragment);
			t2 = space();
			div2 = element("div");
			div1 = element("div");
			t3 = text(t3_value);
			t4 = space();
			t5 = text(t5_value);
			t6 = space();
			table = element("table");
			colgroup = element("colgroup");

			colgroup.innerHTML = `<col span="1" style="width: 20%;"/> 
        <col span="1" style="width: 80%;"/>`;

			t8 = space();
			thead = element("thead");
			tr = element("tr");
			th0 = element("th");
			t9 = text(t9_value);
			t10 = space();
			th1 = element("th");
			t11 = text(t11_value);
			t12 = space();
			tbody = element("tbody");
			if (if_block1) if_block1.c();
			t13 = space();
			div3 = element("div");
			if_block2.c();
			attr(div0, "class", "flex bn-notify-notification-inner svelte-15gl6rk");
			attr(div1, "class", "address-info svelte-15gl6rk");
			attr(th0, "class", "svelte-15gl6rk");
			attr(th1, "class", "svelte-15gl6rk");
			attr(thead, "class", "svelte-15gl6rk");
			attr(tbody, "class", "svelte-15gl6rk");
			attr(table, "class", "balance-change-table table-radius svelte-15gl6rk");
			attr(div2, "class", "details svelte-15gl6rk");
			attr(div3, "class", "details-cta svelte-15gl6rk");
			attr(div4, "class", "maximized radius padding-5 svelte-15gl6rk");
		},
		m(target, anchor) {
			insert(target, div4, anchor);
			if (if_block0) if_block0.m(div4, null);
			append(div4, t0);
			append(div4, div0);
			mount_component(iconbadge, div0, null);
			append(div0, t1);
			mount_component(simulationheader, div0, null);
			append(div4, t2);
			append(div4, div2);
			append(div2, div1);
			append(div1, t3);
			append(div1, t4);
			append(div1, t5);
			append(div2, t6);
			append(div2, table);
			append(table, colgroup);
			append(table, t8);
			append(table, thead);
			append(thead, tr);
			append(tr, th0);
			append(th0, t9);
			append(tr, t10);
			append(tr, th1);
			append(th1, t11);
			append(table, t12);
			append(table, tbody);
			if (if_block1) if_block1.m(tbody, null);
			append(div4, t13);
			append(div4, div3);
			if_blocks[current_block_type_index].m(div3, null);
			current = true;
		},
		p(ctx, [dirty]) {
			if (!/*requireTransactionApproval*/ ctx[0]) {
				if (if_block0) {
					if_block0.p(ctx, dirty);
				} else {
					if_block0 = create_if_block_3(ctx);
					if_block0.c();
					if_block0.m(div4, t0);
				}
			} else if (if_block0) {
				if_block0.d(1);
				if_block0 = null;
			}

			const simulationheader_changes = {};
			if (dirty & /*startTime*/ 16) simulationheader_changes.startTime = /*startTime*/ ctx[4];
			simulationheader.$set(simulationheader_changes);

			if ((!current || dirty & /*$_*/ 128) && t3_value !== (t3_value = /*$_*/ ctx[7]('maximized.balanceChangeHeading', {
				default: en.maximized.balanceChangeHeading
			}) + "")) set_data(t3, t3_value);

			if ((!current || dirty & /*$_*/ 128) && t9_value !== (t9_value = /*$_*/ ctx[7]('maximized.tokenColumnHeader', { default: en.maximized.tokenColumnHeader }) + "")) set_data(t9, t9_value);

			if ((!current || dirty & /*$_*/ 128) && t11_value !== (t11_value = /*$_*/ ctx[7]('maximized.balanceColumnHeader', {
				default: en.maximized.balanceColumnHeader
			}) + "")) set_data(t11, t11_value);

			if (/*balanceChanges*/ ctx[12].length) if_block1.p(ctx, dirty);
			let previous_block_index = current_block_type_index;
			current_block_type_index = select_block_type(ctx);

			if (current_block_type_index === previous_block_index) {
				if_blocks[current_block_type_index].p(ctx, dirty);
			} else {
				group_outros();

				transition_out(if_blocks[previous_block_index], 1, 1, () => {
					if_blocks[previous_block_index] = null;
				});

				check_outros();
				if_block2 = if_blocks[current_block_type_index];

				if (!if_block2) {
					if_block2 = if_blocks[current_block_type_index] = if_block_creators[current_block_type_index](ctx);
					if_block2.c();
				} else {
					if_block2.p(ctx, dirty);
				}

				transition_in(if_block2, 1);
				if_block2.m(div3, null);
			}
		},
		i(local) {
			if (current) return;
			transition_in(iconbadge.$$.fragment, local);
			transition_in(simulationheader.$$.fragment, local);
			transition_in(if_block2);
			current = true;
		},
		o(local) {
			transition_out(iconbadge.$$.fragment, local);
			transition_out(simulationheader.$$.fragment, local);
			transition_out(if_block2);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div4);
			if (if_block0) if_block0.d();
			destroy_component(iconbadge);
			destroy_component(simulationheader);
			if (if_block1) if_block1.d();
			if_blocks[current_block_type_index].d();
		}
	};
}

function instance$2($$self, $$props, $$invalidate) {
	let $_;
	component_subscribe($$self, _, $$value => $$invalidate(7, $_ = $$value));
	let { requireTransactionApproval } = $$props;
	let { transactionApproved } = $$props;
	let { toggleExpanded } = $$props;
	let { destroyApp } = $$props;
	let { simResponse } = $$props;
	let { startTime } = $$props;
	let totalGasInEth = 0;
	let totalGasUsed = 0;
	const device = getDevice();

	const addCommasToNumber = x => {
		const parts = x.toString().split('.');
		parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
		return parts.join('.');
	};

	const cleanBalance = dirtyBalance => {
		const formattedEth = ethers.utils.formatEther(dirtyBalance);
		return roundAndCleanDecimals(formattedEth);
	};

	const roundAndCleanGas = formattedValue => {
		const roundedGwei = parseFloat(formattedValue).toFixed(7);
		return Number(roundedGwei);
	};

	const roundAndCleanDecimals = formattedValue => {
		const roundedGwei = parseFloat(formattedValue).toFixed(6);
		const removeEmptyDecimalPlaces = Number(roundedGwei);
		return addCommasToNumber(removeEmptyDecimalPlaces);
	};

	const shortenAddress = address => {
		return `${address.slice(0, 6)}…${address.slice(-4)}`;
	};

	const cleanGas = gasComputed => {
		const gweiToEther = ethers.utils.formatEther(gasComputed);
		return roundAndCleanGas(gweiToEther);
	};

	const getCumulativeGasInEth = index => {
		if (simResponse.transactions[index].type === 0) {
			$$invalidate(5, totalGasInEth += cleanGas(simResponse.gasUsed[index] * simResponse.transactions[index].gasPrice));
		}
	}; // if (simResponse.transactions[index].type === 2) {
	//   totalGasInEth += cleanGas(
	//     simResponse.gasUsed[index] *
	//       (simResponse.transactions[index].baseFeePerGasGwei +

	//         simResponse.transactions[index].maxPriorityFeePerGasGwei)
	//   )
	// }
	const gasUsed = index => {
		$$invalidate(6, totalGasUsed += simResponse.gasUsed[index]);
	};

	const transactionOriginator = simResponse.transactions[0].from;

	const balanceChanges = simResponse.netBalanceChanges.reduce(
		(arr, changes, index) => {
			if (changes.length) {
				changes.forEach(change => {
					if (change.address.toLowerCase() === transactionOriginator.toLowerCase()) {
						arr.push(change);
					}
				});
			}

			getCumulativeGasInEth(index);
			gasUsed(index);
			return arr;
		},
		[]
	);

	const click_handler = () => {
		destroyApp();
	};

	const func = () => transactionApproved(false);
	const func_1 = () => transactionApproved(true);
	const func_2 = () => toggleExpanded(false);

	$$self.$$set = $$props => {
		if ('requireTransactionApproval' in $$props) $$invalidate(0, requireTransactionApproval = $$props.requireTransactionApproval);
		if ('transactionApproved' in $$props) $$invalidate(1, transactionApproved = $$props.transactionApproved);
		if ('toggleExpanded' in $$props) $$invalidate(2, toggleExpanded = $$props.toggleExpanded);
		if ('destroyApp' in $$props) $$invalidate(3, destroyApp = $$props.destroyApp);
		if ('simResponse' in $$props) $$invalidate(13, simResponse = $$props.simResponse);
		if ('startTime' in $$props) $$invalidate(4, startTime = $$props.startTime);
	};

	return [
		requireTransactionApproval,
		transactionApproved,
		toggleExpanded,
		destroyApp,
		startTime,
		totalGasInEth,
		totalGasUsed,
		$_,
		device,
		cleanBalance,
		shortenAddress,
		transactionOriginator,
		balanceChanges,
		simResponse,
		click_handler,
		func,
		func_1,
		func_2
	];
}

class Maximized extends SvelteComponent {
	constructor(options) {
		super();

		init$1(
			this,
			options,
			instance$2,
			create_fragment$2,
			safe_not_equal,
			{
				requireTransactionApproval: 0,
				transactionApproved: 1,
				toggleExpanded: 2,
				destroyApp: 3,
				simResponse: 13,
				startTime: 4
			},
			add_css$2
		);
	}
}

/* src/views/Minimized.svelte generated by Svelte v3.59.1 */

function add_css$1(target) {
	append_styles(target, "svelte-nbh6v9", ".minimized.svelte-nbh6v9.svelte-nbh6v9{transition:background 300ms ease-in-out, color 300ms ease-in-out;pointer-events:all;backdrop-filter:blur(5px);width:100%;min-height:3.5rem;background:var(--foreground-color);display:flex;flex-direction:column;position:relative;overflow:hidden}.radius.svelte-nbh6v9.svelte-nbh6v9{border-radius:inherit}.tp-header.svelte-nbh6v9.svelte-nbh6v9{padding:0.75rem;border-bottom:var(--border-color, transparent) 1px solid}div.tp-close-btn.svelte-nbh6v9.svelte-nbh6v9{visibility:visible;opacity:1}div.tp-close-btn.svelte-nbh6v9.svelte-nbh6v9{margin-left:auto;margin-bottom:auto;height:1.5rem;width:1.5rem;position:absolute;top:0.5rem;right:0.5rem;justify-content:center;align-items:center}div.minimized.svelte-nbh6v9:hover>div.tp-close-btn-desktop.svelte-nbh6v9{visibility:visible;opacity:1}div.tp-close-btn-desktop.svelte-nbh6v9.svelte-nbh6v9{visibility:hidden;transition:visibility 0.15s linear, opacity 0.15s linear;opacity:0}.tp-close-btn.svelte-nbh6v9 .close-icon.svelte-nbh6v9{width:1.25rem;margin:auto}.tp-close-btn.svelte-nbh6v9>.close-icon.svelte-nbh6v9{color:var(--tp-close-icon-color, var(--onboard-gray-300, var(--gray-300)))}.tp-close-btn.svelte-nbh6v9:hover>.close-icon.svelte-nbh6v9{color:var(--tp-close-icon-hover, var(--onboard-gray-100, var(--gray-100)))}.details-cta.svelte-nbh6v9.svelte-nbh6v9{font-weight:600;font-size:0.875rem;display:flex;justify-content:flex-end;flex-direction:row;align-items:center;padding:0.5rem;gap:0.5rem;height:3rem;border-top:1px solid var(--border-color);flex:none;order:2;align-self:stretch;flex-grow:0}");
}

function create_fragment$1(ctx) {
	let div4;
	let div1;
	let div0;
	let t0;
	let div2;
	let iconbadge;
	let t1;
	let simulationheader;
	let t2;
	let div3;
	let button;
	let current;
	let mounted;
	let dispose;
	iconbadge = new IconBadge({});

	simulationheader = new SimulationHeader({
			props: { startTime: /*startTime*/ ctx[2] }
		});

	button = new Button({
			props: {
				btnText: /*$_*/ ctx[3]('minimized.show', { default: en.minimized.show }),
				btnFunction: /*func*/ ctx[6]
			}
		});

	return {
		c() {
			div4 = element("div");
			div1 = element("div");
			div0 = element("div");
			t0 = space();
			div2 = element("div");
			create_component(iconbadge.$$.fragment);
			t1 = space();
			create_component(simulationheader.$$.fragment);
			t2 = space();
			div3 = element("div");
			create_component(button.$$.fragment);
			attr(div0, "class", "flex items-center close-icon svelte-nbh6v9");
			attr(div1, "class", "tp-close-btn tp-close-btn-" + /*device*/ ctx[4].type + " pointer flex" + " svelte-nbh6v9");
			attr(div2, "class", "flex tp-header svelte-nbh6v9");
			attr(div3, "class", "details-cta svelte-nbh6v9");
			attr(div4, "class", "minimized radius padding-5 svelte-nbh6v9");
		},
		m(target, anchor) {
			insert(target, div4, anchor);
			append(div4, div1);
			append(div1, div0);
			div0.innerHTML = closeIcon;
			append(div4, t0);
			append(div4, div2);
			mount_component(iconbadge, div2, null);
			append(div2, t1);
			mount_component(simulationheader, div2, null);
			append(div4, t2);
			append(div4, div3);
			mount_component(button, div3, null);
			current = true;

			if (!mounted) {
				dispose = listen(div1, "click", stop_propagation(/*click_handler*/ ctx[5]));
				mounted = true;
			}
		},
		p(ctx, [dirty]) {
			const simulationheader_changes = {};
			if (dirty & /*startTime*/ 4) simulationheader_changes.startTime = /*startTime*/ ctx[2];
			simulationheader.$set(simulationheader_changes);
			const button_changes = {};
			if (dirty & /*$_*/ 8) button_changes.btnText = /*$_*/ ctx[3]('minimized.show', { default: en.minimized.show });
			if (dirty & /*toggleExpanded*/ 1) button_changes.btnFunction = /*func*/ ctx[6];
			button.$set(button_changes);
		},
		i(local) {
			if (current) return;
			transition_in(iconbadge.$$.fragment, local);
			transition_in(simulationheader.$$.fragment, local);
			transition_in(button.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(iconbadge.$$.fragment, local);
			transition_out(simulationheader.$$.fragment, local);
			transition_out(button.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div4);
			destroy_component(iconbadge);
			destroy_component(simulationheader);
			destroy_component(button);
			mounted = false;
			dispose();
		}
	};
}

function instance$1($$self, $$props, $$invalidate) {
	let $_;
	component_subscribe($$self, _, $$value => $$invalidate(3, $_ = $$value));
	let { toggleExpanded } = $$props;
	let { destroyApp } = $$props;
	let { startTime } = $$props;
	const device = getDevice();

	const click_handler = () => {
		destroyApp();
	};

	const func = () => toggleExpanded(true);

	$$self.$$set = $$props => {
		if ('toggleExpanded' in $$props) $$invalidate(0, toggleExpanded = $$props.toggleExpanded);
		if ('destroyApp' in $$props) $$invalidate(1, destroyApp = $$props.destroyApp);
		if ('startTime' in $$props) $$invalidate(2, startTime = $$props.startTime);
	};

	return [toggleExpanded, destroyApp, startTime, $_, device, click_handler, func];
}

class Minimized extends SvelteComponent {
	constructor(options) {
		super();

		init$1(
			this,
			options,
			instance$1,
			create_fragment$1,
			safe_not_equal,
			{
				toggleExpanded: 0,
				destroyApp: 1,
				startTime: 2
			},
			add_css$1
		);
	}
}

/* src/views/Index.svelte generated by Svelte v3.59.1 */

function add_css(target) {
	append_styles(target, "svelte-umd5cq", ":host{--font-family-normal:var(--w3o-font-family, Inter, sans-serif);--font-size-4:1.25rem;--font-size-5:1rem;--font-size-6:0.875rem;--font-size-7:0.75rem;--font-line-height-4:12px;--success-500:#5aec99;--danger-400:#ff8080;--spacing-4:1rem;--border-radius-5:8px;--shadow-1:0px 4px 12px rgba(0, 0, 0, 0.1);--tp-z-index:10}.flex{display:flex}.flex-column{flex-direction:column}.items-center{align-items:center}.justify-center{justify-content:center}.relative{position:relative}.pointer{cursor:pointer}.shadow-1{box-shadow:var(--onboard-shadow-1, var(--shadow-1))}*{box-sizing:border-box}button{display:flex;align-items:center;justify-content:center;padding:calc(var(--onboard-spacing-4, var(--spacing-4)) - 1px);border-radius:24px;cursor:pointer;font:inherit;border:none;transition:background-color 150ms ease-in-out, color 150ms ease-in-out}.tp-main.svelte-umd5cq{--text-color:var(--w3o-text-color, white);--action-color:var(--w3o-action-color, var(--primary-400));--border-color:var(--w3o-border-color, var(--gray-500));--background-color:var(--w3o-background-color, var(--gray-700));--foreground-color:var(--w3o-foreground-color, var(--gray-600));--border-radius:var(--w3o-border-radius, 1rem);font-family:var(--onboard-font-family-normal, var(--font-family-normal));border:1px solid transparent;border-color:var(--border-color);background:var(--foreground-color, var(--background-color));color:var(--text-color);border-radius:var(--border-radius);z-index:var(--tp-z-index, inherit)}");
}

// (110:2) {:else}
function create_else_block(ctx) {
	let minimized;
	let current;

	minimized = new Minimized({
			props: {
				toggleExpanded: /*toggleExpanded*/ ctx[5],
				destroyApp: /*destroyApp*/ ctx[2],
				startTime: /*startTime*/ ctx[4]
			}
		});

	return {
		c() {
			create_component(minimized.$$.fragment);
		},
		m(target, anchor) {
			mount_component(minimized, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const minimized_changes = {};
			if (dirty & /*destroyApp*/ 4) minimized_changes.destroyApp = /*destroyApp*/ ctx[2];
			minimized.$set(minimized_changes);
		},
		i(local) {
			if (current) return;
			transition_in(minimized.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(minimized.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(minimized, detaching);
		}
	};
}

// (101:2) {#if expanded || requireTransactionApproval}
function create_if_block(ctx) {
	let maximized;
	let current;

	maximized = new Maximized({
			props: {
				toggleExpanded: /*toggleExpanded*/ ctx[5],
				transactionApproved: /*transactionApproved*/ ctx[6],
				destroyApp: /*destroyApp*/ ctx[2],
				simResponse: /*simResponse*/ ctx[0],
				startTime: /*startTime*/ ctx[4],
				requireTransactionApproval: /*requireTransactionApproval*/ ctx[1]
			}
		});

	return {
		c() {
			create_component(maximized.$$.fragment);
		},
		m(target, anchor) {
			mount_component(maximized, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const maximized_changes = {};
			if (dirty & /*destroyApp*/ 4) maximized_changes.destroyApp = /*destroyApp*/ ctx[2];
			if (dirty & /*simResponse*/ 1) maximized_changes.simResponse = /*simResponse*/ ctx[0];
			if (dirty & /*requireTransactionApproval*/ 2) maximized_changes.requireTransactionApproval = /*requireTransactionApproval*/ ctx[1];
			maximized.$set(maximized_changes);
		},
		i(local) {
			if (current) return;
			transition_in(maximized.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(maximized.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(maximized, detaching);
		}
	};
}

function create_fragment(ctx) {
	let div;
	let current_block_type_index;
	let if_block;
	let div_intro;
	let current;
	const if_block_creators = [create_if_block, create_else_block];
	const if_blocks = [];

	function select_block_type(ctx, dirty) {
		if (/*expanded*/ ctx[3] || /*requireTransactionApproval*/ ctx[1]) return 0;
		return 1;
	}

	current_block_type_index = select_block_type(ctx);
	if_block = if_blocks[current_block_type_index] = if_block_creators[current_block_type_index](ctx);

	return {
		c() {
			div = element("div");
			if_block.c();
			attr(div, "class", "tp-main svelte-umd5cq");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			if_blocks[current_block_type_index].m(div, null);
			current = true;
		},
		p(new_ctx, [dirty]) {
			ctx = new_ctx;
			let previous_block_index = current_block_type_index;
			current_block_type_index = select_block_type(ctx);

			if (current_block_type_index === previous_block_index) {
				if_blocks[current_block_type_index].p(ctx, dirty);
			} else {
				group_outros();

				transition_out(if_blocks[previous_block_index], 1, 1, () => {
					if_blocks[previous_block_index] = null;
				});

				check_outros();
				if_block = if_blocks[current_block_type_index];

				if (!if_block) {
					if_block = if_blocks[current_block_type_index] = if_block_creators[current_block_type_index](ctx);
					if_block.c();
				} else {
					if_block.p(ctx, dirty);
				}

				transition_in(if_block, 1);
				if_block.m(div, null);
			}
		},
		i(local) {
			if (current) return;
			transition_in(if_block);

			if (!div_intro) {
				add_render_callback(() => {
					div_intro = create_in_transition(div, fade, { duration: 250, easing: cubicOut });
					div_intro.start();
				});
			}

			current = true;
		},
		o(local) {
			transition_out(if_block);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div);
			if_blocks[current_block_type_index].d();
		}
	};
}

function instance($$self, $$props, $$invalidate) {
	let expanded;
	let { simResponse } = $$props;
	let { requireTransactionApproval } = $$props;
	let { approved$ } = $$props;
	let { destroyApp } = $$props;
	const device = getDevice();
	const startTime = Date.now();

	function toggleExpanded(maximize) {
		$$invalidate(3, expanded = maximize);
	}

	const transactionApproved = approved => {
		approved$.next(approved);
	};

	$$self.$$set = $$props => {
		if ('simResponse' in $$props) $$invalidate(0, simResponse = $$props.simResponse);
		if ('requireTransactionApproval' in $$props) $$invalidate(1, requireTransactionApproval = $$props.requireTransactionApproval);
		if ('approved$' in $$props) $$invalidate(7, approved$ = $$props.approved$);
		if ('destroyApp' in $$props) $$invalidate(2, destroyApp = $$props.destroyApp);
	};

	$$invalidate(3, expanded = device.type === 'mobile' ? false : true);

	return [
		simResponse,
		requireTransactionApproval,
		destroyApp,
		expanded,
		startTime,
		toggleExpanded,
		transactionApproved,
		approved$
	];
}

class Index extends SvelteComponent {
	constructor(options) {
		super();

		init$1(
			this,
			options,
			instance,
			create_fragment,
			safe_not_equal,
			{
				simResponse: 0,
				requireTransactionApproval: 1,
				approved$: 7,
				destroyApp: 2
			},
			add_css
		);
	}
}

const approved$ = new Subject();
let options;
let optionalSettings;
let app;
const destroyApp = () => {
    app.$destroy();
};
const handleRequireApproval = async (app, fullProviderRequest, req) => {
    const approved = await firstValueFrom(approved$);
    app.$destroy();
    if (!approved) {
        throw new ProviderRpcError({
            code: ProviderRpcErrorCode.ACCOUNT_ACCESS_REJECTED,
            message: 'User rejected the transaction'
        });
    }
    fullProviderRequest(req);
};
const netBalanceChangesExist = (simResp) => {
    if (simResp &&
        simResp.netBalanceChanges &&
        simResp.netBalanceChanges.length) {
        return simResp.netBalanceChanges.some((balChange) => {
            return balChange.length && balChange.length > 0;
        });
    }
    return false;
};
const patchProvider = (walletProvider) => {
    if (!walletProvider) {
        throw new Error(`An EIP 1193 wallet provider is required to preform patching and
      watch for transactions e.g. an injected wallet using window.ethereum`);
    }
    if (walletProvider.simPatched)
        return walletProvider;
    const fullProviderRequest = walletProvider.request;
    const patchedProvider = walletProvider;
    const request = async (req) => {
        if (req.method === 'eth_sendTransaction' &&
            req.params &&
            req.params.length) {
            const transactionParams = req.params;
            await handlePreview(transactionParams, fullProviderRequest, req);
        }
        else {
            return fullProviderRequest(req);
        }
    };
    try {
        patchedProvider.request = request;
        patchedProvider.simPatched = true;
    }
    catch (err) {
        console.error(`There was an error patching the passed in wallet provider.
      The provider may be read only and may be incompatible with Transaction Preview`);
    }
    return patchedProvider;
};
const handlePreview = async (transaction, fullProviderRequest, req) => {
    try {
        if (!options) {
            throw new Error(`Please initialize Transaction Preview package prior to previewing a transaction.
         You can do this by calling the init function with the appropriate params`);
        }
        const preview = await simulateTransactions(options, transaction);
        if (!preview) {
            throw new Error(`An error ocurred while simulating the transaction, please 
      see the console for more details`);
        }
        if (preview.error.length) {
            fullProviderRequest(req);
            handleTPErrors(preview);
        }
        if (preview.status !== 'simulated' || !netBalanceChangesExist(preview)) {
            // If transaction simulation was unsuccessful or balanceChanges do
            // not exist do not create DOM el
            return fullProviderRequest(req);
        }
        if (app)
            app.$destroy();
        app = mountTransactionPreview(preview);
        options.requireTransactionApproval
            ? handleRequireApproval(app, fullProviderRequest, req)
            : fullProviderRequest(req)
                .then(() => {
                app.$destroy();
            })
                .catch(() => app.$destroy());
    }
    catch (e) {
        fullProviderRequest(req);
        if (app)
            app.$destroy();
        throw new Error(`${e}`);
    }
};
const previewTransaction = async (transaction) => {
    try {
        if (!options) {
            throw new Error(`Please initialize Transaction Preview package prior to previewing a transaction.
         You can do this by calling the init function with the appropriate params`);
        }
        const preview = await simulateTransactions(options, transaction);
        if (!preview) {
            throw new Error(`An error ocurred while simulating the transaction, please 
      see the console for more details`);
        }
        if (preview.error.length) {
            handleTPErrors(preview);
        }
        if (preview.status !== 'simulated' || !netBalanceChangesExist(preview)) {
            // If transaction simulation was unsuccessful or balanceChanges do
            // not exist do not create DOM el
            console.error('No net balance changes ocurred from this simulation');
        }
        if (app)
            app.$destroy();
        app = mountTransactionPreview(preview);
        return preview;
    }
    catch (e) {
        if (app)
            app.$destroy();
        throw new Error(`${e}`);
    }
};
const handleTPErrors = (preview) => {
    let internalErrs = preview.internalTransactions.reduce((acc, tx) => {
        if (tx.length) {
            tx.forEach((t) => {
                if (t.errorReason)
                    acc.push(t.errorReason);
            });
        }
        return acc;
    }, []);
    internalErrs = [...preview.error, ...internalErrs];
    throw new Error(`An error occurred during transaction simulation: ${internalErrs.join(' - ')}`);
};
const transactionPreview = (tpOptions) => {
    if (tpOptions) {
        const error = validateTPOptions(tpOptions);
        if (error) {
            throw error;
        }
    }
    // defaults requireTransactionApproval to true
    optionalSettings = { requireTransactionApproval: true, ...tpOptions };
    initialize((tpOptions && tpOptions.i18n) || {});
    return {
        patchProvider,
        init,
        previewTransaction
    };
};
const init = (initOptions) => {
    if (initOptions) {
        const error = validateTPInit(initOptions);
        if (error) {
            throw error;
        }
    }
    options = { ...initOptions, ...optionalSettings };
};
const fontFamilyExternallyDefined = () => {
    if (document.body &&
        (getComputedStyle(document.body).getPropertyValue('--w3o-font-family') ||
            getComputedStyle(document.body).getPropertyValue('--onboard-font-family-normal')))
        return true;
    return false;
};
const importFontsToDoc = async () => {
    const { InterVar } = await import('@web3-onboard/common');
    // Add Fonts to main page
    const styleEl = document.createElement('style');
    styleEl.innerHTML = `
    ${InterVar}
  `;
    document.body.appendChild(styleEl);
};
const mountTransactionPreview = (simResponse) => {
    class TransactionPreviewEl extends HTMLElement {
        constructor() {
            super();
        }
    }
    if (!customElements.get('transaction-preview')) {
        customElements.define('transaction-preview', TransactionPreviewEl);
    }
    if (!fontFamilyExternallyDefined()) {
        importFontsToDoc();
    }
    // add to DOM
    const transactionPreviewDomElement = document.createElement('transaction-preview');
    const target = transactionPreviewDomElement.attachShadow({ mode: 'open' });
    transactionPreviewDomElement.style.all = 'initial';
    const getW3OEl = document.querySelector('onboard-v2');
    const containerElementQuery = options.containerElement || 'body';
    let containerEl;
    // #w3o-transaction-preview-container is the container ID used by Onboard
    if (containerElementQuery !== '#w3o-transaction-preview-container') {
        containerEl = document.querySelector(containerElementQuery);
    }
    else {
        containerEl = getW3OEl.shadowRoot.querySelector(containerElementQuery);
    }
    if (!containerEl) {
        throw new Error(`Element with query ${containerElementQuery} does not exist.`);
    }
    containerEl.appendChild(transactionPreviewDomElement);
    const { requireTransactionApproval } = options;
    const app = new Index({
        target,
        intro: true,
        props: {
            simResponse,
            requireTransactionApproval,
            approved$,
            destroyApp
        }
    });
    return app;
};

export { transactionPreview as default, patchProvider, previewTransaction };
